// Full-screen snap-card rendered inside the Featured reels viewer every
// AD_EVERY_N looks.
//
// Two variants:
//   • "rewarded-gate" (free users) — blocks scroll until the user taps
//     "Watch ad to continue". On reward → onUnlock() unlocks the next batch.
//     This is policy-safe because the ad is user-initiated.
//   • "house-ad" (paid users)     — purely scrollable promo card for credit
//     packs / Pro perks. No third-party network call. Looks like a feed item.
import { useState } from "react";
import { Loader2, PlayCircle, Sparkles, Crown, Coins, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { showRewardedAd } from "@/lib/native-ads";
import { useAuth } from "@/contexts/AuthContext";
import { tapLight, notifySuccess } from "@/lib/haptics";

export const AD_EVERY_N = 20;

interface RewardedGateProps {
  variant: "rewarded-gate";
  batchNumber: number; // 1 = first gate (after 20), 2 = second, …
  onUnlock: () => void;
}

interface HouseAdProps {
  variant: "house-ad";
  batchNumber: number;
  onCtaTap: () => void;
}

type Props = RewardedGateProps | HouseAdProps;

export const ReelsFeedAdCard = (props: Props) => {
  if (props.variant === "rewarded-gate") {
    return <RewardedGateCard {...props} />;
  }
  return <HouseAdCard {...props} />;
};

/* ---------------- Rewarded gate (free users) ---------------- */

const RewardedGateCard = ({ batchNumber, onUnlock }: RewardedGateProps) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleWatch = async () => {
    if (!user || busy) return;
    setBusy(true);
    setFailed(false);
    void tapLight();
    try {
      const result = await showRewardedAd({ userId: user.id });
      if (result.rewarded) {
        void notifySuccess();
        onUnlock();
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const watched = batchNumber * AD_EVERY_N;

  return (
    <article
      className="relative flex h-full w-full snap-start snap-always flex-col items-center justify-center gap-5 bg-gradient-to-br from-primary/25 via-background to-accent/20 px-6 text-center"
      style={{ contain: "layout paint size" }}
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-accent/20 text-primary ring-1 ring-primary/30">
        <Sparkles className="h-8 w-8" />
      </div>

      <div className="space-y-1.5">
        <p className="text-lg font-extrabold leading-tight">
          You've enjoyed {watched} looks
        </p>
        <p className="mx-auto max-w-[280px] text-sm text-muted-foreground">
          Watch a quick ad to keep scrolling — unlocks the next {AD_EVERY_N} featured looks for free.
        </p>
      </div>

      {failed && (
        <p className="rounded-md bg-amber-500/10 px-3 py-2 text-center text-xs text-amber-700 dark:text-amber-300">
          No ad available right now. Please try again in a moment.
        </p>
      )}

      <Button
        onClick={handleWatch}
        disabled={busy}
        size="lg"
        className="w-full max-w-[260px] gap-2 rounded-full"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
        {busy ? "Loading ad…" : failed ? "Try ad again" : "Watch ad to continue"}
      </Button>

      <p className="text-[11px] text-muted-foreground">
        Or <span className="font-bold text-primary">go Pro</span> to remove all ads
      </p>
    </article>
  );
};

/* ---------------- House ad (paid users) ---------------- */

const HOUSE_AD_VARIANTS = [
  {
    icon: Coins,
    title: "Stock up & save",
    body: "Grab a credit pack and try unlimited new looks. Pro users keep the streak going.",
    cta: "See credit packs",
  },
  {
    icon: Crown,
    title: "Get Featured",
    body: "Top creators earn likes, climb the leaderboard, and get spotlighted to thousands.",
    cta: "Submit your look",
  },
];

const HouseAdCard = ({ batchNumber, onCtaTap }: HouseAdProps) => {
  const variant = HOUSE_AD_VARIANTS[(batchNumber - 1) % HOUSE_AD_VARIANTS.length];
  const Icon = variant.icon;

  return (
    <article
      className="relative flex h-full w-full snap-start snap-always flex-col items-center justify-center gap-5 bg-gradient-to-br from-accent/25 via-background to-primary/20 px-6 text-center"
      style={{ contain: "layout paint size" }}
    >
      <span className="absolute left-3 top-[max(env(safe-area-inset-top),12px)] rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/80 backdrop-blur ring-1 ring-white/15">
        Sponsored
      </span>

      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent/30 to-primary/20 text-accent ring-1 ring-accent/30">
        <Icon className="h-8 w-8" />
      </div>

      <div className="space-y-1.5">
        <p className="text-lg font-extrabold leading-tight">{variant.title}</p>
        <p className="mx-auto max-w-[280px] text-sm text-muted-foreground">{variant.body}</p>
      </div>

      <Button onClick={onCtaTap} size="lg" className="w-full max-w-[260px] gap-2 rounded-full">
        <Sparkles className="h-5 w-5" />
        {variant.cta}
      </Button>

      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <ChevronDown className="h-3 w-3" /> Swipe up to keep scrolling
      </p>
    </article>
  );
};
